import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useListings } from "@/contexts/listings-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons";
import { ListingDTO, ListingOwnerType } from "@shaarp/common";
import { FormEvent, useEffect, useState } from "react";
import { ButtonLoading } from "../button-loading";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../ui/command";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Separator } from "../ui/separator";
import { TextareaAutosize } from "../ui/textarea-autosize";
import { useToast, useToastError } from "../ui/use-toast";

export default function DialogUpdateListing({
  listing,
  onClose,
  onUpdated,
  onRemoved,
}: {
  listing: ListingDTO;
  onClose: () => void;
  onUpdated: (listing: ListingDTO) => void;
  onRemoved: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t } = useTranslation();

  const { apiFetchListings } = useListings();
  const { isAdmin, teams } = useTeams();

  const [title, setTitle] = useState<string>(listing.title);
  const [description, setDescription] = useState<string>(listing.description);
  const [ownerType, setOwnerType] = useState<ListingOwnerType>(
    listing.ownerType
  );
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [showTeamsDisabled, setShowTeamsDisabled] = useState(false);
  const [teamsSelected, setTeamsSelected] = useState<string[]>(
    listing.allowedTeams || []
  );
  const [open, setOpen] = useState(false);

  const filterTeams = (value: string, search: string) => {
    return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
  };

  const toggleTeam = (teamId: string) => {
    if (teamsSelected.includes(teamId)) {
      setTeamsSelected(teamsSelected.filter((t) => t !== teamId));
    } else {
      setTeamsSelected([...teamsSelected, teamId]);
    }
  };

  useEffect(() => {
    // Should never be needed while editing, but just in case
    if (listing.teamId && !teamsSelected.includes(listing.teamId)) {
      setTeamsSelected([...teamsSelected, listing.teamId]);
    }
  }, [listing]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { data } = await api.post<ListingDTO>(`/listing/${listing.id}`, {
        listing: {
          title,
          description,
          ownerType,
          allowedTeams: teamsSelected,
        },
      });
      toast({
        variant: "default",
        title: t("listing.dialog.update.success.title", {
          title: listing.title,
        }),
        description: t("listing.dialog.update.success.description"),
      });
      await apiFetchListings({ force: true });
      onUpdated(data);
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const onRemove = async () => {
    try {
      await api.delete(`/listing/${listing.id}`);
      toast({
        variant: "default",
        title: t("listing.dialog.update.delete.success.title", {
          title: listing.title,
        }),
        description: t("listing.dialog.update.delete.success.description"),
      });
      await apiFetchListings();
      onRemoved();
    } catch (error) {
      toastError();
    }
  };

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-[800px] w-full">
          <DialogHeader>
            <DialogTitle>
              {t("listing.dialog.update.title", { title: listing.title })}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit}>
            <Separator className="my-4" />
            <div className="overflow-auto max-h-[70vh] px-3">
              <div className="space-y-5">
                <div className="grid w-full items-center gap-1.5">
                  <Label htmlFor="title">
                    {t("listing.dialog.update.fields.title.label")}
                  </Label>
                  <p className="text-xs text-gray-500">
                    {t("listing.dialog.update.fields.title.helper")}
                  </p>
                  <Input
                    type="text"
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </div>
                <div className="grid w-full items-center gap-1.5">
                  <Label htmlFor="description">
                    {t("listing.dialog.update.fields.description.label")}
                  </Label>
                  <p className="text-xs text-gray-500">
                    {t("listing.dialog.update.fields.description.helper")}
                  </p>
                  <TextareaAutosize
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
                <div className="grid w-full items-center gap-1.5">
                  <Label htmlFor="ownerType">
                    {t("listing.dialog.update.fields.ownerType.label")}
                  </Label>
                  <p className="text-xs text-gray-500">
                    {t("listing.dialog.update.fields.ownerType.helper")}
                  </p>
                  <Select
                    value={ownerType}
                    onValueChange={(newValue) =>
                      setOwnerType(newValue as ListingOwnerType)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue defaultValue={ownerType} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="user">
                          {t(
                            "listing.dialog.update.fields.ownerType.options.user"
                          )}
                        </SelectItem>
                        {isAdmin && (
                          <SelectItem value="shaarp">
                            {t(
                              "listing.dialog.update.fields.ownerType.options.shaarp"
                            )}
                          </SelectItem>
                        )}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                {ownerType === "shaarp" && (
                  <div className="grid w-full items-center gap-1.5">
                    <Popover open={open} onOpenChange={setOpen} modal={true}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={open}
                          aria-label="Select a team"
                          className={cn("justify-between w-[300px]")}
                        >
                          {teamsSelected.length > 0
                            ? t("listing.dialog.update.fields.teams.selected", {
                                count: teamsSelected.length,
                              })
                            : t("listing.dialog.update.fields.teams.select")}
                          <CaretSortIcon className="ml-auto w-4 h-4 opacity-50 shrink-0" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0 overflow-visible">
                        <Command filter={filterTeams}>
                          {teams.length > 5 && (
                            <>
                              <div className="relative">
                                <CommandInput
                                  placeholder={t(
                                    "listing.dialog.update.fields.teams.search"
                                  )}
                                  className="flex-1"
                                  onValueChange={(value) =>
                                    setShowTeamsDisabled(!!value)
                                  }
                                />
                              </div>
                            </>
                          )}
                          <CommandSeparator />
                          <CommandList className="w-full">
                            <CommandGroup key="teamsAdmin">
                              {teams
                                .filter(
                                  (team) =>
                                    team.status === "enabled" ||
                                    showTeamsDisabled
                                )
                                .map((team) => (
                                  <CommandItem
                                    key={team.id}
                                    value={team.name}
                                    disabled={team.id === listing.teamId}
                                    onSelect={() => {
                                      toggleTeam(team.id);
                                    }}
                                  >
                                    <span>
                                      <CheckIcon
                                        className={cn(
                                          "inline-block h-4 w-4 mr-2 text-primary opacity-0",
                                          teamsSelected.includes(team.id) &&
                                            "opacity-100"
                                        )}
                                      />
                                      {team.name}
                                      {team.id === listing.teamId && (
                                        <Badge
                                          variant="outline"
                                          className="ml-2"
                                        >
                                          Propriétaire
                                        </Badge>
                                      )}
                                    </span>
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </div>
            </div>
            <Separator className="my-4" />
            <DialogFooter className="flex justify-between sm:justify-between">
              <Button
                type="button"
                variant="outline"
                className="text-red-500"
                onClick={() => setConfirmDelete(true)}
              >
                {t("listing.dialog.update.delete.button")}
              </Button>
              <span>
                <DialogClose asChild>
                  <Button variant="ghost" type="button">
                    {t("listing.dialog.update.buttons.close")}
                  </Button>
                </DialogClose>
                <ButtonLoading type="submit" isLoading={isLoading}>
                  {t("listing.dialog.update.buttons.update")}
                </ButtonLoading>
              </span>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("listing.dialog.update.delete.title", {
                title: listing.title,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("listing.dialog.update.delete.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmDelete(false)}>
              {t("listing.dialog.update.delete.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => onRemove()}>
              {t("listing.dialog.update.delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
